import { range } from "../../../utils";
import {
  getPhotoUrl,
  type CalendarOptions,
  type ProductTypeConfig,
  type Asset,
  type PhotoRecommendation,
  type PhotoValue,
} from "../../types";
import layouts from "./layout/layouts";
import { ProductThumbnail, Product, ProductPreview } from "./product";
import ProductSettings from "./settings";

interface PromoCodes {
  [key: string]: number;
}

interface Prices {
  calendar: number;
  promoCodes: PromoCodes;
}

const prices: Prices = {
  calendar: 3500,
  promoCodes: {
    FAMILYONE: 35,
    START10: 10,
  },
};

const config: ProductTypeConfig<CalendarOptions> = {
  name: "calendar",
  title: "Calendrier Photo",
  labels: {
    pendingTitle: "Calendriers en cours de création",
    pendingMessage:
      "Vous avez des calendriers en cours de création, voulez vous reprendre leur modification ou créer un nouveau?",
    newButton: "Créer un nouveau calendrier",
  },

  options: {
    pages: {
      defaultValue: {},
    },
    coverPhoto: {
      defaultValue: null,
    },
    theme: {
      defaultValue: null,
    },
    color: {
      defaultValue: null,
    },
    year: {
      defaultValue: 2026,
    },
  },

  price(_options: CalendarOptions, code?: string, shippingPrice: number = 0): number {
    const price = prices.calendar;
    const discount = code && prices.promoCodes[code] ? prices.promoCodes[code] : 0;

    return price - (price * discount) / 100 + shippingPrice;
  },

  priceRows(
    _options: CalendarOptions,
    code?: string,
    shippingPrice: number = 0,
  ): Array<[string, string]> {
    const steps: Array<[string, string]> = [["Calendrier photo", prices.calendar + " DA"]];

    const discount = code && prices.promoCodes[code] ? prices.promoCodes[code] : 0;

    if (discount) {
      steps.push(["Code Promo.", "-" + discount + "%"]);
    }

    steps.push(["Livraison", `${shippingPrice}  DA`]);

    return steps;
  },

  isReady(options: CalendarOptions): boolean {
    return range(12).every((pageIndex) => {
      const page = options.pages[pageIndex];
      if (!page) {
        return false;
      }

      return true;
    });
  },

  photoRecommendation(): PhotoRecommendation {
    return {
      min: 13,
      recommended: 13,
      message: "Nous recommandons 13 photos (couverture + 12 mois)",
    };
  },

  getAssets(options: CalendarOptions): Asset[] {
    const photos: Asset[] = [];
    if (options.coverPhoto) {
      photos.push({
        title: "Couverture",
        url: getPhotoUrl(options.coverPhoto),
        filename: "cover",
      });
    }

    const addPhotos = (
      page: { photos?: Record<number, PhotoValue> },
      index: string,
      keys: number[],
    ) => {
      keys.forEach((key) => {
        if (page.photos && page.photos[key]) {
          photos.push({
            title: `Photo page ${parseInt(index, 10) + 1} - ${key + 1}`,
            url: getPhotoUrl(page.photos[key]),
            filename: `page-${parseInt(index, 10) + 1}-${key + 1}`,
          });
        }
      });
    };

    Object.entries(options.pages || {}).forEach(([index, page]) => {
      const selectedLayout = layouts.find((layout) => layout.value === (page.layout || "margin"));

      if (selectedLayout) {
        addPhotos(page, index, range(selectedLayout.countPhotos));
      }
    });

    return photos;
  },

  settings: ProductSettings,

  edit: Product,

  preview: ProductPreview,

  thumbnail: ProductThumbnail,
};

export default config;
